import { OrganisationCouncilFieldsEdit } from '../../../../types/form-types'
import LookupSearch from '../../../components/wells/lookup-search'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import useUser from '../../../hooks/use-user'
import { getLookupData } from '../../../services/api'
import { CheckableField, CheckableFields, FormField, ReadOnlyField, TypeaheadOption } from '@msaf/core-react'

interface OrganisationCouncilFieldsInterface {
  fields: OrganisationCouncilFieldsEdit
  isLoading: boolean
  setValue: SetValue<OrganisationCouncilFieldsEdit>
  validationErrors: ValidationErrors
  company: string
  isDisabledSection?: boolean
}

export default function OrganisationCouncilFields({
  fields,
  isLoading,
  setValue,
  validationErrors,
  company,
  isDisabledSection,
}: OrganisationCouncilFieldsInterface) {
  const { user } = useUser()
  return (
    <>
      <FormField
        isSkeleton={isLoading}
        label='Regional council'
        labelId={`companyCouncil-${fields.id}`}
        validationMessages={validationErrors?.companyCouncil}
        isRequired
      >
        <LookupSearch
          isSkeleton={isLoading}
          fieldId='organisation'
          labelledBy={`companyCouncil-${fields.id}`}
          value={fields.companyCouncil ?? ''}
          setValue={({ value }) => setValue('companyCouncil', value)}
          isDisabled
        />
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Can create new well records'
        htmlFor={`canCreateWellLabel-${fields.id}`}
        validationMessages={validationErrors?.canCreateWell}
        isRequired
      >
        {!isDisabledSection ? (
          <CheckableFields ariaLabelledBy={`canCreateWellLabel-${fields.id}`} isHorizontal>
            <CheckableField
              id={`canCreateWellYes-${fields.id}`}
              name={`canCreateWell-${fields.id}`}
              describedBy={`canCreateWellLabel-${fields.id}`}
              label='Yes'
              isSkeleton={isLoading}
              type='radio'
              checked={!!fields.canCreateWell}
              onChange={() => setValue('canCreateWell', true)}
            />
            <CheckableField
              id={`canCreateWellNo-${fields.id}`}
              name={`canCreateWell-${fields.id}`}
              describedBy={`canCreateWellLabel-${fields.id}`}
              label='No'
              isSkeleton={isLoading}
              type='radio'
              checked={!fields.canCreateWell}
              onChange={() => setValue('canCreateWell', false)}
            />
          </CheckableFields>
        ) : (
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy={`canCreateWell-${fields.id}`}
            value={fields.canCreateWell}
          />
        )}
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Can edit assigned well records'
        htmlFor={`canEditWellLabel-${fields.id}`}
        validationMessages={validationErrors?.canEditWell}
        isRequired
      >
        {!isDisabledSection ? (
          <CheckableFields ariaLabelledBy={`canEditWellLabel-${fields.id}`} isHorizontal>
            <CheckableField
              id={`canEditWellYes-${fields.id}`}
              name={`canEditWell-${fields.id}`}
              describedBy={`canEditWellLabel-${fields.id}`}
              label='Yes'
              isSkeleton={isLoading}
              type='radio'
              checked={!!fields.canEditWell}
              onChange={() => setValue('canEditWell', true)}
            />
            <CheckableField
              id={`canEditWellNo-${fields.id}`}
              name={`canEditWell-${fields.id}`}
              describedBy={`canEditWellLabel-${fields.id}`}
              label='No'
              isSkeleton={isLoading}
              type='radio'
              checked={!fields.canEditWell}
              onChange={() => setValue('canEditWell', false)}
            />
          </CheckableFields>
        ) : (
          <ReadOnlyField isSkeleton={isLoading} labelledBy={`canEditWell-${fields.id}`} value={fields.canEditWell} />
        )}
      </FormField>
      <FormField
        isSkeleton={isLoading}
        label='Primary contact email'
        htmlFor={`email-${fields.id}`}
        validationMessages={validationErrors?.email}
        isRequired
      >
        <LookupSearch
          selectType='async'
          isSkeleton={isLoading}
          searchFunction={(value: string) =>
            getLookupData('companyContactOptions', user?.token ?? '', user?.auth_method, {
              user: value,
              company: company ?? '',
              council: fields.companyCouncil ?? '',
            })
          }
          fieldId='companyContactOptions'
          labelledBy={`email-${fields.id}`}
          value={`${fields.companyContact ?? ''}`}
          setValue={({ value }: TypeaheadOption) => {
            setValue('companyContact', value)
          }}
          isDisabled={isDisabledSection}
        />
      </FormField>
    </>
  )
}
